import { useState } from "react";
import { registry } from "../../registry";
import type { RequestRunner } from "../../core/requestRunner";
import { ArgForm, type ArgFormValues } from "../ArgForm";

type Props = {
  runner: RequestRunner | undefined;
  selectedContactId: string | undefined;
  onInvoked: (seq: number) => void;
};

export function RequestsPanel({
  runner,
  selectedContactId,
  onInvoked,
}: Props): React.JSX.Element {
  const [argValues, setArgValues] = useState<Record<string, ArgFormValues>>({});
  const [confirming, setConfirming] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<string | undefined>(undefined);

  if (!runner) {
    return (
      <p className="empty-state">
        Not connected yet — requests are unavailable.
      </p>
    );
  }

  const invokable = registry.filter(
    (m) => m.kind !== "interceptor" && m.requests.length > 0,
  );

  const doInvoke = (
    moduleId: string,
    methodName: string,
    key: string,
  ): void => {
    setPending(key);
    runner
      .invoke(moduleId, methodName, argValues[key] ?? {})
      .then((entry) => onInvoked(entry.seq))
      .finally(() => {
        setPending(undefined);
        setConfirming(undefined);
      });
  };

  return (
    <div className="requests-panel">
      {invokable.map((mod) => (
        <fieldset key={mod.id} className="requests-module">
          <legend>{mod.label}</legend>
          {mod.requests.map((r) => {
            const key = `${mod.id}:${r.name}`;
            const values = argValues[key] ?? {};
            return (
              <div
                className={`request-row ${r.mutating ? "mutating" : ""}`}
                key={r.name}
              >
                <p
                  className={`request-name ${r.deprecated ? "deprecated" : ""}`}
                >
                  {r.name}
                  {r.deprecated ? (
                    <span className="deprecated-tag">deprecated</span>
                  ) : null}
                  {r.mutating ? (
                    <span className="mutating-tag">mutates live state</span>
                  ) : null}
                </p>
                <ArgForm
                  spec={r.args}
                  values={values}
                  onChange={(v) =>
                    setArgValues((prev) => ({ ...prev, [key]: v }))
                  }
                  selectedContactId={selectedContactId}
                  idPrefix={key}
                />
                {r.mutating && confirming === key ? (
                  <div className="confirm-row">
                    <span>This changes live agent/contact state. Proceed?</span>
                    <button
                      type="button"
                      disabled={pending === key}
                      onClick={() => doInvoke(mod.id, r.name, key)}
                    >
                      Confirm invoke
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(undefined)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={pending === key}
                    className={r.mutating ? "mutating-invoke" : undefined}
                    onClick={() =>
                      r.mutating
                        ? setConfirming(key)
                        : doInvoke(mod.id, r.name, key)
                    }
                  >
                    {pending === key ? "Invoking…" : "Invoke"}
                  </button>
                )}
              </div>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
